import { useEffect, useMemo, useState } from 'react';
import type { QuizView } from '@elixir/shared';
import { useVoiceShortcuts } from '../keyboard';
import type { SavedSession } from '../state/api';
import { QuizSocket, type QuizSocketState } from '../state/QuizSocket';
import { Store, useStore } from '../state/store';
import type { VoiceSession } from '../voice/VoiceSession';
import { GearIcon, LinkIcon } from './icons';
import { InlineEdit } from './InlineEdit';
import { PeoplePanel } from './PeoplePanel';
import { Scoreboard } from './Scoreboard';
import { SettingsDialog } from './SettingsDialog';
import { Stage } from './Stage';
import { TeamPanel } from './TeamPanel';
import { useToasts, Toasts } from './Toasts';
import { VoiceBar } from './VoiceBar';

export interface RoomContext {
  quiz: QuizView;
  me: QuizView['participants'][number] | undefined;
  isHost: boolean;
  send: QuizSocket['send'];
  voice: VoiceSession;
}

export function RoomView({
  session,
  voice,
  onLeave,
  onSessionInvalid,
}: {
  session: SavedSession;
  voice: VoiceSession;
  onLeave: () => void;
  onSessionInvalid: () => void;
}) {
  const isHost = Boolean(session.hostKey);
  const [socket, setSocket] = useState<QuizSocket | null>(null);
  const toasts = useToasts();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [panel, setPanel] = useState<'scores' | 'people'>('scores');

  // State channel. Created in an effect so it is torn down cleanly with the view.
  useEffect(() => {
    const s = new QuizSocket(session);
    s.onForceMute = () => {
      voice.setMicMuted(true);
      toasts.push('The quizmaster muted you. Unmute when you are ready to talk.');
    };
    s.onKicked = () => void voice.stop();
    voice.onWhisperMode = (active) => s.send({ type: 'whisper', active });
    voice.onAnnounceMode = (active) => s.send({ type: 'announce', active });
    setSocket(s);
    return () => s.close();
  }, [session, voice, toasts]);

  useEffect(() => {
    const leave = () => void voice.stop();
    window.addEventListener('pagehide', leave);
    return () => window.removeEventListener('pagehide', leave);
  }, [voice]);

  useVoiceShortcuts(voice, isHost);

  const v = useStore(voice.store);
  const sock = useStore(socket?.store ?? EMPTY_SOCKET);
  const quiz = sock.quiz;
  const me = quiz?.participants.find((p) => p.id === session.participantId);

  // Follow team changes from the quizmaster.
  useEffect(() => {
    if (quiz) voice.syncTeam(me?.teamId ?? null);
  }, [voice, quiz, me?.teamId]);

  useEffect(() => {
    if (quiz && !isHost) voice.setAnnouncingFlag(quiz.announcing);
  }, [voice, quiz?.announcing, isHost, quiz]);

  useEffect(() => {
    if (sock.lastError) {
      toasts.push(sock.lastError);
      socket?.clearError();
    }
  }, [sock.lastError, socket, toasts]);

  // Our participant vanished from a fresh snapshot: session no longer valid.
  useEffect(() => {
    if (quiz && !me && !sock.fatal) onSessionInvalid();
  }, [quiz, me, sock.fatal, onSessionInvalid]);

  const ctx = useMemo<RoomContext | null>(
    () => (quiz && socket ? { quiz, me, isHost, send: socket.send.bind(socket), voice } : null),
    [quiz, me, isHost, socket, voice],
  );

  const ended = sock.fatal ?? (v.phase === 'ended' ? (v.problem ?? 'You left the quiz.') : null);
  if (ended) {
    return (
      <div className="center-screen">
        <div className="panel narrow">
          <h1>Disconnected</h1>
          <p>{ended}</p>
          {!sock.fatal && (
            <button className="btn btn-primary" onClick={onLeave}>
              Rejoin
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!ctx) return <div className="center-screen">Loading the room…</div>;

  const myTeam = quiz!.teams.find((t) => t.id === me?.teamId);
  const inviteUrl = `${location.origin}/q/${quiz!.code}`;
  const connected = v.phase === 'connected' && sock.connection === 'open';

  return (
    <div className="room" style={{ ['--team' as string]: myTeam?.color ?? 'var(--ink)' }}>
      <header className="topbar">
        <span className="wordmark small">Elixir</span>
        {isHost ? (
          <InlineEdit className="quiz-title" value={quiz!.name} label="Quiz name" onSave={(name) => ctx.send({ type: 'renameQuiz', name })} />
        ) : (
          <span className="quiz-title">{quiz!.name}</span>
        )}
        <div className="topbar-right">
          <span className={`conn ${connected ? 'ok' : 'warn'}`} title={connected ? 'Connected' : 'Reconnecting'}>
            {connected ? 'Connected' : v.phase === 'reconnecting' || sock.connection === 'retrying' ? 'Reconnecting…' : 'Connecting…'}
          </span>
          <button
            className="btn btn-quiet"
            onClick={() => {
              void navigator.clipboard?.writeText(inviteUrl);
              toasts.push('Invite link copied.');
            }}
            title={inviteUrl}
          >
            <LinkIcon /> <span className="code">{quiz!.code}</span>
          </button>
          <button className="icon-btn" onClick={() => setSettingsOpen(true)} aria-label="Audio settings">
            <GearIcon />
          </button>
        </div>
      </header>

      {v.problem && (
        <div className="notice notice-bad banner" role="alert">
          <p>{v.problem}</p>
          <button className="link-btn" onClick={() => voice.clearProblem()}>
            Dismiss
          </button>
        </div>
      )}

      <main className="room-main">
        <Stage ctx={ctx} screen={v.screen} announcing={quiz!.announcing} />
        <aside className="sidebar">
          {isHost ? (
            <>
              <div className="tabs" role="tablist">
                <button role="tab" aria-selected={panel === 'scores'} onClick={() => setPanel('scores')}>
                  Scores
                </button>
                <button role="tab" aria-selected={panel === 'people'} onClick={() => setPanel('people')}>
                  Teams &amp; people
                </button>
              </div>
              {panel === 'scores' ? <Scoreboard ctx={ctx} /> : <PeoplePanel ctx={ctx} speakers={v.publicSpeakers} />}
            </>
          ) : (
            <>
              <Scoreboard ctx={ctx} />
              <TeamPanel ctx={ctx} publicSpeakers={v.publicSpeakers} teamSpeakers={v.teamSpeakers} teamRoom={v.teamRoom} />
            </>
          )}
        </aside>
      </main>

      <VoiceBar ctx={ctx} state={v} />

      {settingsOpen && (
        <SettingsDialog
          voice={voice}
          state={v}
          isHost={isHost}
          hostLink={session.hostKey ? `${inviteUrl}#host=${session.hostKey}` : null}
          onClose={() => setSettingsOpen(false)}
          onLeave={onLeave}
          onCopied={(msg) => toasts.push(msg)}
        />
      )}
      <Toasts toasts={toasts} />
    </div>
  );
}

const EMPTY_SOCKET = new Store<QuizSocketState>({ quiz: null, connection: 'connecting', fatal: null, lastError: null });
